import { ChangeDetectionStrategy, Component, Input, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WaterInfoImageType } from '@models/water-image-types';
import type { WaterExpectationSaveRequest } from '@models/water-information';
import { TranslatePipe } from '@ngx-translate/core';
import { ImageFieldComponent } from '@shared/components/image-picker/image-field.component';
import { LocalizedListEditorComponent } from '@shared/components/localized-list-editor/localized-list-editor.component';
import { LocalizedTextEditorComponent } from '@shared/components/localized-text-editor/localized-text-editor.component';
import type { GeneralForm } from '../water-information-form.types';

@Component({
  selector: 'app-water-information-general-section',
  templateUrl: './water-information-general-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatTooltipModule,
    TranslatePipe,
    ImageFieldComponent,
    LocalizedTextEditorComponent,
    LocalizedListEditorComponent,
  ],
})
export class WaterInformationGeneralSectionComponent {
  @Input({ required: true }) form!: FormGroup<GeneralForm>;

  readonly appHeaderPhotoName = input.required<string | null>();
  readonly appHeaderPhotoUrl = input.required<string | null>();
  readonly ambianceVideoImageName = input.required<string | null>();
  readonly ambianceVideoImageUrl = input.required<string | null>();
  readonly expectations = input.required<WaterExpectationSaveRequest[]>();

  readonly expectationsChange = output<WaterExpectationSaveRequest[]>();
  readonly uploadImage = output<{ type: WaterInfoImageType; file: File }>();
  readonly deleteImage = output<WaterInfoImageType>();

  protected readonly WaterInfoImageType = WaterInfoImageType;
  readonly langs = ['nl', 'en', 'de', 'it', 'fr'];

  addExpectation(): void {
    this.expectationsChange.emit([
      ...this.expectations(),
      { id: null, available: false, textNl: null, textEn: null, textDe: null, textIt: null, textFr: null },
    ]);
  }

  removeExpectation(index: number): void {
    this.expectationsChange.emit(this.expectations().filter((_, i) => i !== index));
  }

  setExpectationAvailable(index: number, value: boolean): void {
    this.expectationsChange.emit(this.expectations().map((e, i) => (i === index ? { ...e, available: value } : e)));
  }

  setExpectationText(index: number, lang: string, value: string): void {
    const field = `text${lang.charAt(0).toUpperCase()}${lang.slice(1)}` as keyof WaterExpectationSaveRequest;
    this.expectationsChange.emit(
      this.expectations().map((e, i) => (i === index ? { ...e, [field]: value || null } : e)),
    );
  }
}
